import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { X } from "lucide-react";
import { useFocusTrap, useGlobalKeydown, useRestoreFocus } from "../lib/keyboard";

// 對話框。aria-modal 必須配焦點陷阱,否則等於沒做無障礙。
// 關閉後焦點回到原本的觸發元素。動畫只在使用者沒要求減少動態時才播。

const STYLE_ID = "nq-dialog-motion";

// 動畫整段包在 prefers-reduced-motion: no-preference 內,沒開偏好就完全不動。
const MOTION_CSS = `
@media (prefers-reduced-motion: no-preference) {
  @keyframes nq-dialog-fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes nq-dialog-pop {
    from { opacity: 0; transform: translateY(-6px) scale(0.99); }
    to   { opacity: 1; transform: none; }
  }
  .nq-dialog-backdrop { animation: nq-dialog-fade 120ms ease-out; }
  .nq-dialog-panel { animation: nq-dialog-pop 140ms ease-out; }
}
`;

/** 動畫樣式只插一次,不隨每個對話框重複塞進 head。 */
function useDialogMotionStyle(active: boolean): void {
  useEffect(() => {
    if (!active || typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = MOTION_CSS;
    document.head.appendChild(style);
  }, [active]);
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** 可見標題。有標題時用 aria-labelledby 指向它。 */
  title?: ReactNode;
  /** 沒有可見標題時的無障礙名稱。 */
  label?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** 最大寬度的 Tailwind 類別。 */
  maxWidth?: string;
  /** 是否顯示右上角關閉鈕。 */
  showClose?: boolean;
  /** 內容區樣式,預設有內距;指令面板這類自排版的畫面可覆寫。 */
  contentClassName?: string;
  /** 對話框外框的額外樣式。 */
  className?: string;
  /** 點背景是否關閉。破壞性確認類對話框可關掉。 */
  closeOnBackdrop?: boolean;
}

export function Dialog({
  open,
  onClose,
  title,
  label,
  children,
  footer,
  maxWidth = "max-w-lg",
  showClose = true,
  contentClassName = "px-4 py-4",
  className = "",
  closeOnBackdrop = true,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useDialogMotionStyle(open);
  useFocusTrap(panelRef, open);
  useRestoreFocus(open);

  // Esc 關閉。掛在 window 上,焦點在哪都收得到。
  useGlobalKeydown((event) => {
    if (event.key === "Escape") onClose();
  }, open);

  // 開啟時把焦點移進對話框,鍵盤使用者不必自己 Tab 進來。
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    if (!panel.contains(document.activeElement)) panel.focus();
  }, [open]);

  // 對話框開著時鎖住背景捲動。
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
      {/* 背景遮罩不是唯一的關閉路徑,另有 Esc 與關閉鈕。 */}
      <div
        aria-hidden="true"
        onClick={closeOnBackdrop ? onClose : undefined}
        className="nq-dialog-backdrop fixed inset-0 bg-[rgba(28,25,23,0.42)]"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title ? undefined : label}
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={
          `nq-dialog-panel relative z-10 my-8 w-full ${maxWidth} rounded-[var(--radius)] ` +
          `border border-[var(--border)] bg-[var(--surface)] shadow-card ${className}`
        }
      >
        {title || showClose ? (
          <div className="flex items-start justify-between gap-3 border-b border-[var(--border)] px-4 py-3">
            {title ? (
              <h2 id={titleId} className="m-0 text-base font-semibold text-[var(--text)]">
                {title}
              </h2>
            ) : (
              <span />
            )}
            {showClose ? (
              <button
                type="button"
                onClick={onClose}
                aria-label="關閉"
                className="rounded-[var(--radius)] p-1 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
              >
                <X aria-hidden="true" size={18} />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className={contentClassName}>{children}</div>
        {footer ? (
          <div className="flex justify-end gap-2 border-t border-[var(--border)] px-4 py-3">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
